import * as React from 'react';
import { forwardRef, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import sx from '../../sx.js';
import { themeGet } from '../../utils/theme-get.js';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';
import RuleStepLabel from './RuleStepLabel.js';
import RuleStepConnector from './RuleStepConnector.js';
import { useRuleBuilderStepsContext } from './context/RuleBuilderStepsContext.js';
import Box from '../Box/Box.js';
import Spacer from '../Spacer/Spacer.js';

const StyledStep = styled(Box).withConfig({
  displayName: "RuleBuilderStep__StyledStep",
  componentId: "sc-m0lkhv-0"
})(["position:relative;max-width:100%;padding:8px;display:flex;flex-direction:column;align-items:flex-start;justify-content:flex-start;&:hover{cursor:pointer;}&:focus-visible{outline:none;box-shadow:0 0 0 2px ", ";}", ""], themeGet('colors.border.focused'), sx);
const StepContent = styled(Box).withConfig({
  displayName: "RuleBuilderStep__StepContent",
  componentId: "sc-m0lkhv-1"
})(["width:100%;margin-left:", ";"], themeGet('space.2'));
const RuleBuilderStep = /*#__PURE__*/forwardRef(({
  children,
  index,
  isSelected: isSelectedProp,
  label,
  onSelect,
  isLastStep,
  isSecondToLastStep,
  isCompletedStep,
  stepRef,
  condition = 'when',
  ...props
}, forwardedRef) => {
  const containerRef = useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, containerRef);
  const {
    isError,
    isLoading,
    onSelectStep,
    activeStep
  } = useRuleBuilderStepsContext();
  const [isFocused, setIsFocused] = useState(false);
  const handleSelect = i => {
    if (onSelectStep) {
      onSelectStep(i);
    }
  };
  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleBlur = () => {
    setIsFocused(false);
  };
  const isActive = isSelectedProp || isFocused || index === activeStep;
  useEffect(() => {
    stepRef === null || stepRef === void 0 ? void 0 : stepRef(containerRef.current);
  }, [containerRef, stepRef]);
  let directionValue;
  if (index === 0) directionValue = 'down';else if (index === 1) directionValue = 'right';else directionValue = 'down';
  return /*#__PURE__*/React.createElement(StyledStep
  // aria-selected={isSelectedProp}
  // aria-labelledby={`step-label-${label}`}
  , {
    onClick: () => handleSelect(index),
    as: "li",
    tabIndex: isActive ? 0 : -1,
    onFocus: handleFocus,
    onBlur: handleBlur,
    isSelected: isActive
    // @TODO: TS has bested me, we need to properly fwd ref
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    ref: containerRef
  }, /*#__PURE__*/React.createElement(Box, {
    style: {
      width: '100%',
      display: 'flex',
      alignItems: 'center'
    }
  }, isLastStep ? /*#__PURE__*/React.createElement(Box, {
    as: "div",
    sx: {
      mt: '-24px',
      ml: '8px',
      pb: 1,
      pl: 2
    }
  }, /*#__PURE__*/React.createElement(AnimatePresence, {
    exitBeforeEnter: true
  }, /*#__PURE__*/React.createElement(RuleStepLabel, {
    condition: condition,
    isSelected: isActive,
    label: label
  }))) : /*#__PURE__*/React.createElement(Box, {
    as: "div"
  }, /*#__PURE__*/React.createElement(AnimatePresence, {
    exitBeforeEnter: true
  }, /*#__PURE__*/React.createElement(RuleStepLabel, {
    condition: condition,
    isSelected: isActive,
    isCompletedStep: isCompletedStep,
    label: label
  })))), /*#__PURE__*/React.createElement(RuleStepConnector, {
    index: index,
    isSelectedStep: isActive,
    isCompletedStep: isCompletedStep,
    isLastStep: isLastStep,
    direction: directionValue,
    sx: {
      ml: 1,
      mt: 2
    }
  }, /*#__PURE__*/React.createElement(StepContent, null, children, /*#__PURE__*/React.createElement(Spacer, {
    axis: "vertical",
    size: isSecondToLastStep ? 32 : 20
  }))));
});
RuleBuilderStep.displayName = 'RuleBuilderStep';
var RuleBuilderStep$1 = RuleBuilderStep;

export { RuleBuilderStep$1 as default };
